import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import {
  Input,
  MaskedInput,
  Modal,
  ModalCloseButton,
  Select,
} from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import { Order } from 'contracts/Orders';
import { FindSchedulingDocks } from 'contracts/Scheduling';
import { addDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth, useSchedulingDocks, useValidation } from 'hooks';
import {
  ChangeEvent,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import { UpdateOrderDriverAndVehicleActions as MainActions } from 'store/ducks/orders';
import { UpdateOrderDriverAndVehicleValidator as Validator } from 'validators/Orders';
import * as S from './styles';

export interface Ref {
  openModal: () => void;
}

interface Props {
  order: Order;
  onUpdate?: () => void;
}

const formatDate = (isoDate: string): string => {
  // Formata a data para 'H:mm'
  return format(new Date(isoDate), 'H:mm', { locale: ptBR });
};

interface IUpdateDriverAndVehicleModal
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

const UpdateDriverAndVehicleModal: IUpdateDriverAndVehicleModal = forwardRef(
  (props, ref) => {
    const { order, onUpdate } = props;
    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);
    const { handleFormErrors, handleApiErrors } = useValidation();
    const { profile } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [scheduledAt, setScheduledAt] = useState<string | null>(
      order.scheduledAt
    );
    const [schedulingOptionsMock, setSchedulingOptionsMock] = useState<any[]>(
      []
    );

    const isAdmin = profile?.type === 'admin';
    const editDisabled =
      Boolean(order?.approvedAt || order?.canceledAt) && !isAdmin;

    const [docksQuery, setDocksQuery] = useState<Partial<FindSchedulingDocks>>({
      warehouseId: order.warehouse.id,
      carrierId: order.carrier.id,
      companyId: order.company.id,
      orderTypeId: order.orderType.id,
      operationTypeId: order.operationType.id,
      date: order.scheduledAt,
      duration: order.duration,
      orderId: order.id,
    });

    const {
      schedulingDocksOptions,
      loadingSchedulingDocks,
      fetchSchedulingDocks,
    } = useSchedulingDocks();

    useEffect(() => {
      fetchSchedulingDocks(docksQuery);
    }, [fetchSchedulingDocks, docksQuery]);

    useEffect(() => {
      if (
        schedulingDocksOptions.length &&
        schedulingDocksOptions[0].options.length
      ) {
        setSchedulingOptionsMock(schedulingDocksOptions);
      } else {
        if (scheduledAt) {
          setSchedulingOptionsMock([
            {
              label: order.dock.name,
              options: [
                {
                  label: `${order.dock.name} às ${formatDate(scheduledAt)}`,
                  value: `${order.dock.id}|${scheduledAt}`,
                },
              ],
            },
          ]);
        }
      }
    }, [order.dock.id, order.dock.name, scheduledAt, schedulingDocksOptions]);

    const { loading: updatingOrder, validationErrors } = useSelector(
      (state: RootState) => state.updateOrderDriverAndVehicle
    );

    const onModalClose = useCallback((): void => {
      setIsOpen(false);
      dispatch(MainActions.reset());
      formRef.current?.setErrors({});
      formRef.current?.reset();
    }, [dispatch]);

    const setFormValues = useCallback((): void => {
      const {
        driverName,
        driverDocument,
        vehiclePlate,
        cellPhone,
        scheduledAt,
      } = order;
      if (isOpen) {
        formRef.current?.setData({
          driverName,
          driverDocument,
          vehiclePlate,
          cellPhone,
          date: scheduledAt.split('T')[0],
        });
      }
    }, [isOpen, order]);

    const onSuccess = useCallback((): void => {
      formRef.current?.reset();
      onModalClose();
      onUpdate?.();
    }, [onModalClose, onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});

          const { schema } = new Validator();

          const validData = await schema.validate(data, {
            abortEarly: false,
          });

          const formatterValidData = {
            ...validData,
            scheduledAt: validData.dockAndTimeframe.split('|')[1],
          };

          dispatch(
            MainActions.request(order.id, formatterValidData, onSuccess)
          );
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, handleFormErrors, onSuccess, order.id]
    );

    useEffect(() => {
      setFormValues();
    }, [setFormValues]);

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    useEffect(() => {
      return () => {
        dispatch(MainActions.reset());
      };
    }, [dispatch]);

    useImperativeHandle(
      ref,
      () => ({
        openModal: (): void => {
          setIsOpen(true);
        },
      }),
      []
    );

    const onDateChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>): void => {
        const { value } = e.target;
        formRef.current?.setFieldValue('dockAndTimeframe', null);
        return setDocksQuery((state) => ({
          ...state,
          date: !!value ? value : undefined,
        }));
      },
      []
    );

    const onDockAndTimeframeChange = useCallback((option: any): void => {
      if (!option) {
        return;
      }

      const [dockId, timeframe] = option.value.split('|');
      setScheduledAt(timeframe);
      return setDocksQuery((state) => ({
        ...state,
        dockId,
      }));
    }, []);

    return (
      <Modal isOpen={isOpen} onClickOutside={onModalClose}>
        <S.ModalContent style={{ maxWidth: '720px' }}>
          <S.ModalHeader>
            <h4>Editar motorista e placa</h4>
            <ModalCloseButton onClick={onModalClose} />
          </S.ModalHeader>
          <S.ModalBody>
            <Form ref={formRef} onSubmit={onSubmit}>
              <S.FormRow>
                <Input name="driverName" label="Nome do motorista" />
                <MaskedInput
                  name="cellPhone"
                  label="Celular"
                  mask="(99) 99999-9999"
                />
              </S.FormRow>
              <S.FormRow>
                <MaskedInput
                  name="driverDocument"
                  label="CPF do motorista"
                  mask="999.999.999-99"
                />
                <Input name="vehiclePlate" label="Placa do veículo" />
              </S.FormRow>
              <S.FormRow>
                <Input
                  name="date"
                  type="date"
                  label="Data"
                  // min={new Date().toISOString().split('T')[0]}
                  max={addDays(new Date(), 30).toISOString().split('T')[0]}
                  onChange={onDateChange}
                  disabled={editDisabled}
                />
                <Select
                  name="dockAndTimeframe"
                  label="Doca e horário"
                  value={
                    schedulingOptionsMock.length
                      ? schedulingOptionsMock[0].options.find(
                          (dockAndTimeFrame: any) =>
                            dockAndTimeFrame.value ===
                            `${order.dock.id}|${scheduledAt}`
                        )
                      : null
                  }
                  options={schedulingOptionsMock}
                  isLoading={loadingSchedulingDocks}
                  isDisabled={!schedulingDocksOptions.length || editDisabled}
                  onChange={(e) => onDockAndTimeframeChange(e)}
                />
              </S.FormRow>
              <S.FormActions>
                <S.Button type="button" mood="light" onClick={onModalClose}>
                  {MODAL_DISMISS_ACTION}
                </S.Button>
                <S.Button type="submit" disabled={updatingOrder}>
                  {updatingOrder ? <S.ActivityIndicator /> : 'Salvar'}
                </S.Button>
              </S.FormActions>
            </Form>
          </S.ModalBody>
        </S.ModalContent>
      </Modal>
    );
  }
);

export default UpdateDriverAndVehicleModal;
